// Quick Create IP Object Modal
// Minimal IP object creation form for inline use from other forms
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useState, useEffect, useRef } from 'react';
import Modal, {
  ModalFormField,
  ModalInput,
  ModalSelect,
  ModalTextarea,
  ModalPrimaryButton,
  ModalSecondaryButton
} from '../shared/Modal';
import { apiPost, apiFetch } from '../../utils/api';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';

const QuickCreateIpObjectModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    description: '',
  });
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const formRef = useRef(null);

  // Fetch categories when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Keyboard shortcuts for quick submit
  useKeyboardShortcuts({
    'ctrl+Enter': () => {
      if (formRef.current && formData.name && formData.category_id && !loading) {
        formRef.current.requestSubmit();
      }
    },
    'cmd+Enter': () => {
      if (formRef.current && formData.name && formData.category_id && !loading) {
        formRef.current.requestSubmit();
      }
    },
  }, isOpen);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await apiFetch('/api/v1/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(Array.isArray(data) ? data : (data.categories || []));
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiPost('/api/v1/ip_objects', {
        ip_object: {
          name: formData.name,
          category_id: formData.category_id,
          description: formData.description || null,
          status: 'active',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.join(', ') || 'Failed to create IP object');
      }

      const newIpObject = await response.json();

      // Reset form
      setFormData({ name: '', category_id: '', description: '' });

      // Notify parent
      onCreated(newIpObject);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', category_id: '', description: '' });
    setError(null);
    onClose();
  };

  const icon = (
    <svg className="w-4 h-4 text-[#a1c6a2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const footer = (
    <>
      <ModalPrimaryButton
        type="submit"
        form="quick-create-ip-object-form"
        disabled={loading || !formData.name || !formData.category_id}
        loading={loading}
      >
        Create IP Object
      </ModalPrimaryButton>
      <ModalSecondaryButton type="button" onClick={handleClose}>
        Cancel
      </ModalSecondaryButton>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Quick Add IP Object"
      subtitle="New Asset"
      icon={icon}
      footer={footer}
      maxWidth="sm:max-w-md"
    >
      <form id="quick-create-ip-object-form" ref={formRef} onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 p-3 border border-[#c47474] bg-[rgba(196,116,116,0.1)] text-[#c47474] text-sm">
            {error}
          </div>
        )}

        <ModalFormField label="Asset Name" required>
          <ModalInput
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Muhammad Ali Collection"
            required
            autoFocus
          />
        </ModalFormField>

        <ModalFormField label="Category" required>
          <ModalSelect
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
            required
            disabled={loadingCategories}
          >
            <option value="">
              {loadingCategories ? 'Loading...' : '-- Select Category --'}
            </option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </ModalSelect>
        </ModalFormField>

        <ModalFormField label="Description">
          <ModalTextarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Brief description of the IP asset (optional)"
            rows={3}
          />
        </ModalFormField>

        <p
          className="text-[10px] text-[rgba(255,251,247,0.3)] mt-4"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          Press Cmd+Enter to submit
        </p>
      </form>
    </Modal>
  );
};

export default QuickCreateIpObjectModal;
